// apps/web/src/features/russia-requests/queryKeys.ts
import type { RussiaRequestAuditEntriesQuery, RussiaRequestListQuery } from '@magimp/shared';

/**
 * @aiPurpose React Query key factory for the russia-requests feature (the
 *   CRUD hooks live in `queries.ts`, the realtime cache-narrowing helpers in
 *   `cacheNarrowing.ts`). Key shapes are consumed by `RealtimeBootstrap`'s
 *   `russia-requests:updated` handler to invalidate the affected detail and
 *   the row-containing lists; changing them here without updating the
 *   realtime handlers silently breaks live invalidation.
 * @aiInvariant every mutation `onSuccess` invalidates both the list
 *   prefix `['russia-requests']` and the affected detail
 *   `['russia-request', id]` so any list-filter combination stays
 *   consistent regardless of which filter set is currently in cache.
 */
export const russiaRequestsKeys = {
  all: ['russia-requests'] as const,
  list: (filters: Partial<RussiaRequestListQuery>) => ['russia-requests', filters] as const,
  // WHY (#943): the infinite (load-more) cache stores `InfiniteData<T>`,
  //   not a bare `T`. Sharing the `list` key would force every consumer
  //   to branch on the cache shape; a dedicated sub-prefix keeps both
  //   variants distinguishable while remaining under `russiaRequestsKeys.all`
  //   so the realtime `russia-requests:updated` handler still invalidates
  //   the infinite list with no extra wiring.
  listInfinite: (filters: Partial<RussiaRequestListQuery>) =>
    ['russia-requests', 'infinite', filters] as const,
  detail: (id: string) => ['russia-request', id] as const,
  auditEntries: (id: string, filters: Partial<RussiaRequestAuditEntriesQuery>) =>
    ['russia-request', id, 'audit-entries', filters] as const,
  carriers: (id: string) => ['russia-request', id, 'carriers'] as const,
  // WHY (#1548): COMPLEX cargo section reads the request's file list.
  files: (id: string) => ['russia-request', id, 'files'] as const,
  // WHY (#1548): SIMPLE cargo read view resolves each position photo's
  //   signed download URL on demand, keyed by (request, item, objectKey).
  itemPhotoDownloadUrl: (id: string, itemId: string, objectKey: string) =>
    ['russia-request', id, 'item-photo-download-url', itemId, objectKey] as const,
  // WHY (#1067): RF-scoped chat keys are deliberately disjoint from the
  //   China `request-chat` keys so realtime fan-outs and invalidations
  //   cross-talk only through the prefix the bootstrap explicitly maps.
  chat: (id: string) => ['russia-request-chat', id] as const,
  chatPrefix: () => ['russia-request-chat'] as const,
  chatUnreadSummary: (russiaRequestIds: readonly string[]) =>
    ['russia-request-chat-unread-summary', [...russiaRequestIds].sort()] as const,
  chatUnreadSummaryPrefix: () => ['russia-request-chat-unread-summary'] as const,
  // WHY (#706 Slice B): a reconnect / lifecycle catch-up has no
  //   `selectedId` mirror for the Russia domain yet, so it cannot
  //   invalidate a specific detail key the way the China side does.
  //   Exposing the detail prefix lets `invalidateRequestCaches` refresh
  //   every cached Russia detail at once, which is the only way an open
  //   `/russia-rates/:id` drawer stays consistent after the socket
  //   re-establishes from bfcache or a transient disconnect.
  detailPrefix: () => ['russia-request'] as const,
};
